/**
 * 자랑하기 — 판 결과를 그림 한 장으로 그려 폰 공유 창으로 보낸다(작업지시서 5절 「결과 공유 카드」).
 *
 * 🎯 그림은 브라우저가 그린다(스위티 결정 2026-11-04). 서버는 그림을 만들지도 저장하지도 않는다.
 * 🚫 카카오 SDK 를 쓰지 않는다 — 앱 키와 남의 스크립트가 필요하다. 폰 공유 창에 카톡이 이미 있다.
 * 카드에는 3글자 이름표도 싣지 않는다 — 점수 · 최고 아이템 · 수만 싣는다.
 */

export interface ShareData {
    /** game = 끝난 판 결과 · item = 방금 만든 아이템(결과 팝업) */
    kind: 'game' | 'item';
    score: number;
    /** 첫 발견 · 첫 전설 · 이야기 속 물건 같은 한 줄(아이템 자랑에서만) */
    badge?: string | null;
    best: { emoji: string; name: string; gradeLabel: string; color: string; textColor: string } | null;
    madeCount: number;
    firsts: number;
    url: string;
}

export type ShareOutcome = 'shared' | 'saved' | 'cancelled';

const W = 1080;
const H = 1350;
const POP = '#c53a6b';
const EMOJI_FONT = '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';

function textFont(): string {
    return getComputedStyle(document.body).fontFamily || 'sans-serif';
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null); // 그림이 없어도 카드는 그린다
        img.src = src;
    });
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}

export async function drawCard(data: ShareData): Promise<Blob> {
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas unavailable');

    await document.fonts?.ready;
    const font = textFont();
    const mascot = await loadImage('/images/pop/bangul-happy.webp');

    const bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, '#fff5f8');
    bg.addColorStop(1, '#fbd5e3');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';

    ctx.fillStyle = POP;
    ctx.font = `800 60px ${font}`;
    ctx.fillText('연금술사가 되었다', W / 2, 140);
    ctx.fillStyle = '#9f2c55';
    ctx.font = `600 34px ${font}`;
    ctx.fillText('Pop · 제이드아이', W / 2, 192);

    if (mascot) ctx.drawImage(mascot, W / 2 - 120, 230, 240, 240);

    ctx.fillStyle = '#6b7280';
    ctx.font = `600 40px ${font}`;
    ctx.fillText(data.kind === 'item' ? '지금 점수' : '이번 판 점수', W / 2, 540);
    ctx.fillStyle = POP;
    ctx.font = `800 150px ${font}`;
    ctx.fillText(data.score.toLocaleString(), W / 2, 690);

    // 이번 판 최고 아이템
    ctx.fillStyle = '#ffffff';
    roundRect(ctx, 140, 740, W - 280, 330, 48);
    ctx.fill();
    if (data.best) {
        ctx.strokeStyle = data.best.color;
        ctx.lineWidth = 10;
        roundRect(ctx, 140, 740, W - 280, 330, 48);
        ctx.stroke();

        ctx.fillStyle = '#6b7280';
        ctx.font = `600 34px ${font}`;
        ctx.fillText(data.kind === 'item' ? '방금 만든 아이템' : '이번 판 최고 아이템', W / 2, 800);
        ctx.font = `120px ${EMOJI_FONT}`;
        ctx.fillText(data.best.emoji, W / 2, 940);
        ctx.fillStyle = '#111827';
        ctx.font = `800 56px ${font}`;
        ctx.fillText(data.best.name, W / 2, 1010);
        ctx.fillStyle = data.best.textColor;
        ctx.font = `800 38px ${font}`;
        ctx.fillText(data.best.gradeLabel, W / 2, 1055);
    } else {
        ctx.fillStyle = '#6b7280';
        ctx.font = `600 44px ${font}`;
        ctx.fillText('이번 판은 몸풀기였어요', W / 2, 920);
    }

    if (data.badge) {
        ctx.fillStyle = '#b45309';
        ctx.font = `800 46px ${font}`;
        ctx.fillText(data.badge, W / 2, 1160);
    } else {
        ctx.fillStyle = '#374151';
        ctx.font = `700 42px ${font}`;
        ctx.fillText(`만든 아이템 ${data.madeCount}개 · 첫 발견 ${data.firsts}개`, W / 2, 1160);
    }

    ctx.fillStyle = '#9ca3af';
    ctx.font = `600 32px ${font}`;
    ctx.fillText(data.url.replace(/^https?:\/\//, ''), W / 2, 1275);

    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))), 'image/png');
    });
}

export function shareText(data: ShareData): string {
    if (data.kind === 'item' && data.best) {
        const badge = data.badge ? ` ${data.badge}` : '';
        return `「연금술사가 되었다」에서 ${data.best.emoji} ${data.best.name}(${data.best.gradeLabel}) 만들기 성공!${badge} 한 판 해 보세요 👉 ${data.url}`;
    }
    const best = data.best ? ` ${data.best.emoji} ${data.best.name}(${data.best.gradeLabel})까지 만들었어요.` : '';
    return `「연금술사가 되었다」에서 ${data.score.toLocaleString()}점을 냈어요!${best} 한 판 해 보세요 👉 ${data.url}`;
}

/**
 * 폰이면 공유 창(그림 + 글 + 링크), 공유 창이 없으면 그림을 내려받고 링크를 복사한다.
 */
export async function shareResult(data: ShareData): Promise<ShareOutcome> {
    const blob = await drawCard(data);
    const file = new File([blob], `pop-alchemist-${data.score}.png`, { type: 'image/png' });
    const text = shareText(data);

    if (navigator.canShare?.({ files: [file] })) {
        try {
            await navigator.share({ files: [file], title: '연금술사가 되었다', text });
            return 'shared';
        } catch (e) {
            if (e instanceof DOMException && e.name === 'AbortError') return 'cancelled';
            // 그 밖의 실패는 저장으로 넘어간다
        }
    }

    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1000);
    try {
        await navigator.clipboard?.writeText(text);
    } catch {
        // 복사를 막은 브라우저 — 그림은 이미 저장했다
    }

    return 'saved';
}
